import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Autocomplete, Grid, TextField, Typography, Switch } from "@mui/material";
import * as Yup from "yup";
import { Form, Formik, useFormikContext } from "formik";
import { sendDataWithGet, sendDataWithPost } from "../../../services/services";
import FormDialog from "../../FormDialog";
import Label from "../../Label";
import { showSuccessMsg } from "../../../utils/toast";
import { validateAmount } from "../../../utils/appUtils";
import { COLORS } from "../../../utils/colors";
import { styles } from "../../../utils/styles";

/**
 * Helper function to find and extract a list of currency strings
 * from the currencies object array structure in the returned API data.
 * @param {Array} terminalList - List of terminals that have been fetched.
 * @param {number} terminalId - ID of the selected terminal.
 * @param {number} paymentHostId - ID of the selected payment host.
 * @returns {Array<string>} Array containing currency names (e.g., ["SGD", "THB"]).
 */
const getCurrencies = (terminalList, terminalId, paymentHostId) => {
  const terminal = terminalList.find((t) => t.id === terminalId);
  if (!terminal || !terminal.payment_mapping_terminal) {
    return [];
  }

  // Find the correct payment mapping
  const paymentMapping = terminal.payment_mapping_terminal.find(
    (pm) => pm.payment_host?.id === paymentHostId
  );

  if (!paymentMapping || !paymentMapping.currencies) {
    return [];
  }

  // Map the array of objects currencies to the array of currency name strings
  return paymentMapping.currencies.map(c => c.currency);
};

// =========================================================================
// CUSTOM HOOK: Encapsulates useEffect logic requiring Formik's context
// =========================================================================
const useInitialFeesSetup = (id, terminalList, initialValues) => {
  // Access Formik's context
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    // Condition to run:
    // 1. ADD Mode (!id): Always run when terminal/host are selected and terminalList is ready.
    // 2. EDIT Mode (id): Run ONLY if initial fees are EMPTY/MISSING, and terminalList is ready.
    
    // Check if we are in a state that requires fees setup/re-setup:
    const requiresSetup = 
      (!id && terminalList?.length > 0 && values.payment_host?.id) || // ADD Mode setup
      (id && initialValues.fees.length === 0 && terminalList?.length > 0 && values.payment_host?.id); // EDIT Mode, missing initial fees

    // Only run if the condition is met
    if (requiresSetup) {
      // The logic here extracts currencies and creates the fees structure using terminalList.

      const terminalId = values.terminal.id;
      const paymentHostId = values.payment_host.id;

      // Note: getCurrencies uses terminalList initialized in the main component's useEffect
      const currencyNames = getCurrencies(
        terminalList,
        terminalId,
        paymentHostId
      );

      // Create new fees structure, mapping existing fees from initialValues (only relevant in EDIT mode)
      const newFees = currencyNames.map((cName) => {
        // Find fee value already in initialValues (from API details on load)
        const existingFee = initialValues.fees.find(f => f.currency === cName);

        return {
          currency: cName,
          // Keep the value from API if present, otherwise use "0"
          transaction_fee: existingFee?.transaction_fee || "0",
          refund_fee: existingFee?.refund_fee || "0",
          chargeback_fee: existingFee?.chargeback_fee || "0",
        };
      });

      // Overwrite values.fees with the loaded currency list and mapped old values
      setFieldValue("fees", newFees);
    }
    // Added explicit dependencies
  }, [id, terminalList, values.payment_host?.id, setFieldValue, initialValues.fees.length, values.terminal.id]);
};
// =========================================================================


const CreateDialog = ({ data = {}, onClose, cb }) => {
  const {
    id,
    merchant,
    terminal,
    payment_host,
    payment_brand,
    payment_cycle,
    minimum_payout_amount,
    currencies_data
  } = data?.details || {};

  const [list, setList] = useState({});
  const [terminalList, setTerminalList] = useState([]);
  const [paymentHostList, setPaymentHostList] = useState([]);

  /**
   * API call function to get terminals by Merchant ID.
   * Accepts an optional terminalId for single fetch (Edit mode).
   */
  const getTerminalsByMerchant = useCallback((id, terminalId = null, cb) => {
    const url = "/bank_account/merchant_payout_info/terminal_by_merchant/";
    const payload = { merchant_id: id };
    if (terminalId) {
      payload.terminal_id = terminalId;
    }
    sendDataWithPost(url, payload, cb);
  }, []); // Empty dependency array means this function is stable

  // Static data loading (Merchant list, Payout Cycle list,...)
  useEffect(() => {
    const url = "/bank_account/merchant_payout_info/static";
    sendDataWithGet(url, setList);
  }, []);

  // MEMOIZED initialValues: Ensures stability and fixes 'initialValues is not defined'
  const initialValues = useMemo(() => {
    const fees = data?.details?.currencies_data?.map((c) => ({
      currency: c.currency,
      transaction_fee: c.transaction_fee || "0",
      refund_fee: c.refund_fee || "0",
      chargeback_fee: c.chargeback_fee || "0",
    })) || [];

    return {
      merchant: merchant ? { id: merchant.id, label: merchant.name } : "",
      terminal: terminal ? { id: terminal.id, label: terminal.terminal_id } : "",
      payment_host: payment_host ? { id: payment_host.id, label: payment_host.name } : "",
      payment_brand: payment_brand ? { id: payment_brand.id, label: payment_brand.name } : "",
      payment_cycle: payment_cycle ? { id: payment_cycle, label: payment_cycle } : "",
      is_active: data?.details?.hasOwnProperty('is_active') ? data.details.is_active : true,
      fees: fees,
    };
  }, [data.details]); // Only re-calculate when data.details changes

  // Logic to load terminalList and paymentHostList when in Edit/Add Mode
  useEffect(() => {
    // Case 1: Edit Mode - Fetch only one terminal
    if (id && merchant?.id && terminal?.id) {
      // FIX: Use the terminal data from the initial fetch (data.details)
      // The API should return the enriched terminal object inside data.details
      const enrichedTerminal = {
        id: terminal.id,
        terminal_id: terminal.terminal_id,
        // The core change: Use payment_mapping_terminal from data.details
        payment_mapping_terminal: data.details.payment_mapping_terminal
      };
      
      // Initialize terminalList for use by getCurrencies/useInitialFeesSetup hook
      setTerminalList([enrichedTerminal]);
      
      // Initialize Payment Host List with only current Host
      setPaymentHostList([{ payment_host: payment_host }]);
    }
    // Case 2: Add Mode - Fetch all terminals if merchant is pre-selected
    else if (!id && merchant?.id) {
      // Fetch all terminals for the selected merchant
      getTerminalsByMerchant(merchant.id, null, setTerminalList);
    }
    // Added getTerminalsByMerchant to dependencies for stability, though it's memoized
  }, [id, merchant?.id, terminal, payment_host, getTerminalsByMerchant, data.details]); // Add data.details as dependency


  // =========================================================================
  // MAIN RENDER SECTION
  // =========================================================================

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        merchant: Yup.object().required("Merchant is required"),
        terminal: Yup.object().required("Terminal ID is required"),
        payment_host: Yup.object().required("Payment Host is required"),
        payment_brand: Yup.object().required("Payment Brand is required"),
        payment_cycle: Yup.object().required("Payment Cycle is required"),
        fees: Yup.array().of(
          Yup.object().shape({
            currency: Yup.string().required(),
            transaction_fee: Yup.string()
              .test("valid-amount", "Invalid amount", (value) => {
                return !value || validateAmount(value);
              }),
            refund_fee: Yup.string()
              .test("valid-amount", "Invalid amount", (value) => {
                return !value || validateAmount(value);
              }),
            chargeback_fee: Yup.string()
              .test("valid-amount", "Invalid amount", (value) => {
                return !value || validateAmount(value);
              }),
          })
        ),
      })}
      onSubmit={async (values, { setSubmitting }) => {
        const url = "/bank_account/merchant_payout_info_pending/request/";
        const payload = {
          merchant: values.merchant.id,
          terminal: values.terminal.id,
          payment_host: values.payment_host.id,
          payment_brand: values.payment_brand.id,
          payment_cycle: values.payment_cycle.id,
          is_active: values.is_active,
          action: id ? "update" : "add",
          pk: id || "",
          currencies_data: values.fees.map(fee => ({
            currency: fee.currency,
            transaction_fee: fee.transaction_fee || "0",
            refund_fee: fee.refund_fee || "0",
            chargeback_fee: fee.chargeback_fee || "0",
          }))
        };

        await sendDataWithPost(url, payload, null, null, () => {
          showSuccessMsg();
          cb();
          onClose();
        });
        setSubmitting(false);
      }}
      enableReinitialize={true}
    >
      {/* FIX: Pass necessary functions (getTerminalsByMerchant) 
        and state variables (paymentHostList) to the wrapper component.
      */}
      <FormikContentWrapper
        id={id}
        terminalList={terminalList}
        paymentHostList={paymentHostList} // <-- FIX: Pass paymentHostList
        initialValues={initialValues}
        getTerminalsByMerchant={getTerminalsByMerchant} // <-- FIX: Pass getTerminalsByMerchant
        getCurrencies={getCurrencies}
        list={list}
        setTerminalList={setTerminalList}
        setPaymentHostList={setPaymentHostList}
        onClose={onClose}
        cb={cb}
        data={data}
      />
    </Formik>
  );
};

// Component to wrap the form content and use FormikContext (Custom Hook)
const FormikContentWrapper = ({
  id,
  terminalList,
  paymentHostList, // <-- Destructure FIX
  initialValues,
  getCurrencies,
  list,
  setTerminalList,
  setPaymentHostList,
  getTerminalsByMerchant, // <-- Destructure FIX
  data,
  onClose
}) => {
  const {
    values, touched, errors, isSubmitting, handleChange, handleBlur, setFieldValue, handleSubmit
  } = useFormikContext();

  // Call the custom hook here to execute the fee setup logic
  useInitialFeesSetup(id, terminalList, initialValues);

  // Call API to get Terminal List when Merchant changes
  // FIX: Removed useCallback wrapper as the function is passed to a non-hook context
  const handleMerchantChange = (value) => {
    setFieldValue(
      "merchant",
      !value ? "" : { id: value.id, label: value.label }
    );

    setFieldValue("terminal", "");
    setTerminalList([]);
    setFieldValue("payment_host", "");
    setFieldValue("payment_brand", "");
    setPaymentHostList([]);
    setFieldValue("fees", []);

    if (value) {
      // Use passed function
      getTerminalsByMerchant(value.id, null, setTerminalList);
    }
  };

  // Logic when Terminal changes (Filter Payment Host list from existing terminalList)
  const handleTerminalChange = (value) => {
    setFieldValue(
      "terminal",
      !value ? "" : { id: value.id, label: value.label }
    );
    setFieldValue("payment_host", "");
    setFieldValue("payment_brand", "");
    setPaymentHostList([]);
    setFieldValue("fees", []);

    if (value) {
      const selectedTerminalData = terminalList.find(t => t.id === value.id);

      // Filter out existing payment hosts and remove duplicates
      const payment_mapping_terminal =
        (selectedTerminalData?.payment_mapping_terminal?.filter(
          (item) => item.payment_host
        ) || [])
          .filter(
            (item, index, self) =>
              index ===
              self.findIndex((t) => t.payment_host?.id === item.payment_host?.id)
          );
      setPaymentHostList(payment_mapping_terminal);
    }
  };

  // Update this function to handle the new Currency structure
  const handlePaymentHostChange = (value) => {
    setFieldValue(
      "payment_host",
      !value ? "" : { id: value.id, label: value.label }
    );

    setFieldValue("fees", []);

    if (values.terminal && value) {
      const terminalId = values.terminal.id;
      const paymentHostId = value.id;

      // Use passed function
      const currencyNames = getCurrencies(
        terminalList,
        terminalId,
        paymentHostId
      );

      // Create new fee structure
      const newFees = currencyNames.map((cName) => ({
        currency: cName,
        transaction_fee: "0",
        refund_fee: "0",
        chargeback_fee: "0",
      }));

      setFieldValue("fees", newFees);
    }
  };

  const handlePaymentBrandChange = (value) => {
    setFieldValue(
      "payment_brand",
      !value ? "" : { id: value.id, label: value.label }
    );
    setFieldValue("payment_host", "");
    setFieldValue("fees", []);
  }

  const isSubmitDisabledByData = !values.fees || values.fees.length === 0;

  const filteredPaymentHosts = paymentHostList
    ?.filter(item => item?.payment_brand?.id === values.payment_brand?.id)
    .map(item => ({
      id: item?.payment_host?.id,
      label: item?.payment_host?.name,
    })) || [];

  return (
    <FormDialog
      open={true}
      onClose={onClose} // Use prop from CreateDialog
      title={
        data?.mode === "view" ? "Merchant Payout Details" : (id ? "Edit Merchant Payout Info" : "Add Merchant Payout Info")
      }
      scroll="paper"
      handleSubmit={handleSubmit}
      loading={isSubmitting}
      disabledSubmit={data?.mode === "view" || isSubmitDisabledByData}
      showActions={data?.mode !== "view"} // Hide actions in view mode
    >
      <Form autoComplete="off" noValidate>
        <Grid item sm={12} xs={12} sx={{ py: 2, px: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Typography variant="h6" my={1}>
              Merchant Info
            </Typography>

            <Grid container spacing={2} alignItems="center">
              {/* Merchant Field */}
              <Grid item xs={3}>
                <Typography align="right">
                  Merchant {!id && "*"}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                {!id ? (
                  <Autocomplete
                    options={
                      list?.merchant?.map((item) => ({
                        id: item.id,
                        label: item.name,
                      })) || []
                    }
                    value={values.merchant}
                    loadingText="Loading..."
                    disabled={isSubmitting}
                    onChange={(e, value) => {
                      handleMerchantChange(value);
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return option.id === value.id;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="merchant"
                        size="small"
                        fullWidth
                        placeholder="Search..."
                        onBlur={handleBlur}
                        error={Boolean(
                          touched.merchant && errors.merchant
                        )}
                        helperText={
                          touched.merchant && errors.merchant
                        }
                      />
                    )}
                  />
                ) : (
                  <Typography>{values.merchant.label}</Typography>
                )}
              </Grid>

              {/* Terminal ID Field */}
              <Grid item xs={3}>
                <Typography align="right">
                  Terminal ID {!id && "*"}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                {!id ? (
                  <Autocomplete
                    options={
                      terminalList?.map((item) => ({
                        id: item.id,
                        label: item.terminal_id,
                      })) || []
                    }
                    value={values.terminal}
                    loadingText="Loading..."
                    disabled={isSubmitting || !values.merchant}
                    sx={{
                      "& .MuiInputBase-root.Mui-disabled": {
                        ...( !values.merchant && { backgroundColor: COLORS.disabledBg })
                      },
                    }}
                    onChange={(e, value) => {
                      handleTerminalChange(value);
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return option.id === value.id;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="terminal"
                        size="small"
                        fullWidth
                        placeholder="Search..."
                        onBlur={handleBlur}
                        error={Boolean(
                          touched.terminal && errors.terminal
                        )}
                        helperText={
                          touched.terminal && errors.terminal
                        }
                      />
                    )}
                  />
                ) : (
                  <Typography>{values.terminal.label}</Typography>
                )}
              </Grid>

              {/* Payment Brand Field */}
              <Grid item xs={3}>
                <Typography align="right">
                  Payment Brand {!id && "*"}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                {!id ? (
                  <Autocomplete
                    options={
                      // FIX: paymentHostList is now available via props/destructuring
                      paymentHostList?.map((item) => ({
                        id: item?.payment_brand?.id,
                        label: item?.payment_brand?.name,
                      })) || []
                    }
                    value={values.payment_brand}
                    loadingText="Loading..."
                    disabled={isSubmitting || !values.terminal}
                    sx={{
                      "& .MuiInputBase-root.Mui-disabled": {
                        ...( !values.terminal && { backgroundColor: COLORS.disabledBg })
                      },
                    }}
                    onChange={(e, value) => {
                      handlePaymentBrandChange(value);
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return option?.id === value?.id;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="payment_brand"
                        size="small"
                        fullWidth
                        placeholder="Search..."
                        onBlur={handleBlur}
                        error={Boolean(
                          touched.payment_brand && errors.payment_brand
                        )}
                        helperText={
                          touched.payment_brand && errors.payment_brand
                        }
                      />
                    )}
                  />
                ) : (
                  <Typography>{values.payment_brand.label}</Typography>
                )}
              </Grid>

              {/* Payment Host Field */}
              <Grid item xs={3}>
                <Typography align="right">
                  Payment Host {!id && "*"}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                {!id ? (
                  <Autocomplete
                    options={filteredPaymentHosts}
                    value={values.payment_host}
                    loadingText="Loading..."
                    disabled={isSubmitting || !values.payment_brand}
                    sx={{
                      "& .MuiInputBase-root.Mui-disabled": {
                        ...( !values.payment_brand && { backgroundColor: COLORS.disabledBg })
                      },
                    }}
                    onChange={(e, value) => {
                      handlePaymentHostChange(value);
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return option.id === value.id;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="payment_host"
                        size="small"
                        fullWidth
                        placeholder="Search..."
                        onBlur={handleBlur}
                        error={Boolean(
                          touched.payment_host && errors.payment_host
                        )}
                        helperText={
                          touched.payment_host && errors.payment_host
                        }
                      />
                    )}
                  />
                ) : (
                  <Typography>{values.payment_host.label}</Typography>
                )}
              </Grid>
            </Grid>

            {/* ... (Payout Info Section) ... */}
            <Typography variant="h6" my={1}>
              Payout Info
            </Typography>

            <Grid container spacing={2} alignItems="center">
              {/* Payout Cycle Field */}
              <Grid item xs={3}>
                <Typography align="right">Payout Cycle *</Typography>
              </Grid>
              <Grid item xs={3}>
                <Autocomplete
                  options={
                    list?.payment_cycle?.map((item) => ({
                      id: item.id,
                      label: item.name,
                    })) || []
                  }
                  value={values.payment_cycle}
                  loadingText="Loading..."
                  disabled={data?.mode === "view" || isSubmitting}
                  onChange={(e, value) => {
                    setFieldValue(
                      "payment_cycle",
                      !value
                        ? ""
                        : { id: value.id, label: value.label }
                    );
                  }}
                  isOptionEqualToValue={(option, value) => {
                    return option.id === value.id;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="payment_cycle"
                      size="small"
                      fullWidth
                      placeholder="Search..."
                      onBlur={handleBlur}
                      error={Boolean(
                        touched.payment_cycle && errors.payment_cycle
                      )}
                      helperText={
                        touched.payment_cycle && errors.payment_cycle
                      }
                    />
                  )}
                />
              </Grid>

              {/* Is Active Field */}
              {!!id && (
                <>
                  <Grid item xs={3}>
                    <Typography align="right">Active</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Label color={values.is_active ? "success" : "error"}>
                      {values.is_active ? "YES" : "NO"}
                    </Label>
                  </Grid>
                </>
              )}

              {/* Fees for Currencies */}
              {(values.payment_host && values.fees && values.fees.length > 0) ? (
                <Grid item xs={12}>
                  <Typography variant="h6" mt={2} mb={1}>Service Fees</Typography>

                  {/* -------------------- START: FEES -------------------- */}
                  {values.fees.map((fee, index) => (
                    <React.Fragment key={index}>
                      {/* Currency Label & Dropdown (Disabled) */}
                      <Grid container spacing={2} alignItems="center" sx={{ mt: index > 0 ? 2 : 0 }} >
                        <Grid item xs={3}>
                          <Typography align="right" >
                            Currency
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Autocomplete
                            options={[{ id: fee.currency, label: fee.currency }]}
                            value={{ id: fee.currency, label: fee.currency }}
                            loadingText="Loading..."
                            disabled={true}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name={`fees[${index}].currency`}
                                size="small"
                                fullWidth
                                value={fee.currency}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={6}></Grid>
                      </Grid>

                      {/* Transaction Fee & Refund Fee (Input Fields) */}
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={3}>
                          <Typography align="right">
                            Transaction Fee ({fee.currency})
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            label={``}
                            margin="dense"
                            fullWidth
                            name={`fees[${index}].transaction_fee`}
                            size="small"
                            color="secondary"
                            value={fee.transaction_fee}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(
                              touched.fees?.[index]?.transaction_fee &&
                              errors.fees?.[index]?.transaction_fee
                            )}
                            helperText={
                              touched.fees?.[index]?.transaction_fee &&
                              errors.fees?.[index]?.transaction_fee
                            }
                            disabled={data?.mode === "view" || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Typography align="right">
                            Refund Fee ({fee.currency})
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            label={``}
                            margin="dense"
                            fullWidth
                            name={`fees[${index}].refund_fee`}
                            size="small"
                            color="secondary"
                            value={fee.refund_fee}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(
                              touched.fees?.[index]?.refund_fee &&
                              errors.fees?.[index]?.refund_fee
                            )}
                            helperText={
                              touched.fees?.[index]?.refund_fee &&
                              errors.fees?.[index]?.refund_fee
                            }
                            disabled={data?.mode === "view" || isSubmitting}
                          />
                        </Grid>
                      </Grid>

                      {/* Chargeback Fee */}
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={3}>
                          <Typography align="right">
                            Chargeback Fee ({fee.currency})
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            label={``}
                            margin="dense"
                            fullWidth
                            name={`fees[${index}].chargeback_fee`}
                            size="small"
                            color="secondary"
                            value={fee.chargeback_fee}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(
                              touched.fees?.[index]?.chargeback_fee &&
                              errors.fees?.[index]?.chargeback_fee
                            )}
                            helperText={
                              touched.fees?.[index]?.chargeback_fee &&
                              errors.fees?.[index]?.chargeback_fee
                            }
                            disabled={data?.mode === "view" || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={6}></Grid>
                      </Grid>

                    </React.Fragment>
                  ))}
                  {/* -------------------- END: FEES -------------------- */}

                </Grid>

              ) : (
                values.payment_host && (
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="h6" mb={1}>Service Fees</Typography>
                    <Typography color="error" variant="body1">
                      ⚠️ No currency data found for the selected route.
                    </Typography>
                  </Grid>
                )
              )}
            </Grid>
          </Grid>
        </Grid>
      </Form>
    </FormDialog>
  );
};


export default CreateDialog;